/**
 * PTPFilterEngine - Standalone canvas-based filter engine
 * Ported from the gallerix PhotoArt filter system
 * Self-contained class with all helpers and filter implementations
 */
class PTPFilterEngine {
  constructor() {
    this._filters = [
      { id: 'original',      name: 'ORIGINAL',  isNew: false, defaultStrength: 100 },
      { id: 'golden',        name: 'SUNSET',    isNew: false, defaultStrength: 100 },
      { id: 'pine',          name: 'GALLERY',   isNew: false, defaultStrength: 100 },
      { id: 'eternity',      name: 'MIST',      isNew: false, defaultStrength: 100 },
      { id: 'coral',         name: 'BLUSH',     isNew: false, defaultStrength: 100 },
      { id: 'moon',          name: 'LUNA',      isNew: false, defaultStrength: 100 },
      { id: 'forest',        name: 'RUSTIC',    isNew: false, defaultStrength: 100 },
      { id: 'vintage',       name: 'ANTIQUE',   isNew: false, defaultStrength: 100 },
      { id: 'blackwhite',    name: 'B & W',     isNew: false, defaultStrength: 100 },
      { id: 'sepia',         name: 'VINTAGE',   isNew: false, defaultStrength: 100 },
      { id: 'warm',          name: 'WARM',      isNew: false, defaultStrength: 100 },
      { id: 'cool',          name: 'COOL',      isNew: false, defaultStrength: 100 },
      { id: 'popart',        name: 'POP ART',   isNew: false, defaultStrength: 100 },
      { id: 'vintageposter', name: 'RETRO',     isNew: false, defaultStrength: 100 },
      { id: 'stencil',       name: 'STENCIL',   isNew: false, defaultStrength: 100 }
    ];
  }

  // ============================================================
  // PUBLIC API
  // ============================================================

  /**
   * Returns the list of available filters
   * @returns {Array} Filter definitions with id, name, isNew, defaultStrength
   */
  getFilters() {
    return this._filters.map(f => ({ ...f }));
  }

  /**
   * Generate 80x80 center-cropped thumbnails for each filter
   * Processes one filter per animation frame to avoid blocking UI
   * @param {HTMLImageElement|HTMLCanvasElement} sourceImage - Source image
   * @param {Function} callback - Called with (filterId, dataURL) for each thumbnail
   */
  generateThumbnails(sourceImage, callback) {
    const thumbSize = 80;
    const filters = this._filters;
    let index = 0;

    // Determine source dimensions
    const srcWidth = sourceImage.width || sourceImage.naturalWidth;
    const srcHeight = sourceImage.height || sourceImage.naturalHeight;

    // Calculate center crop for square thumbnail
    let sx, sy, sWidth, sHeight;
    if (srcWidth > srcHeight) {
      sHeight = srcHeight;
      sWidth = srcHeight;
      sx = (srcWidth - sWidth) / 2;
      sy = 0;
    } else {
      sWidth = srcWidth;
      sHeight = srcWidth;
      sx = 0;
      sy = (srcHeight - sHeight) / 2;
    }

    const processNext = () => {
      if (index >= filters.length) return;

      const filter = filters[index];
      const canvas = document.createElement('canvas');
      canvas.width = thumbSize;
      canvas.height = thumbSize;
      const ctx = canvas.getContext('2d');

      // Draw center-cropped square
      ctx.drawImage(sourceImage, sx, sy, sWidth, sHeight, 0, 0, thumbSize, thumbSize);

      // Apply filter if not original
      if (filter.id !== 'original') {
        const imageData = ctx.getImageData(0, 0, thumbSize, thumbSize);
        const data = imageData.data;
        this.applyFilterById(data, filter.id, 1.0, thumbSize, thumbSize);
        ctx.putImageData(imageData, 0, 0);
      }

      callback(filter.id, canvas.toDataURL('image/jpeg', 0.85));

      index++;
      requestAnimationFrame(processNext);
    };

    requestAnimationFrame(processNext);
  }

  /**
   * Apply a filter to a source canvas and return a new canvas with the result
   * @param {HTMLCanvasElement} sourceCanvas - Source canvas element
   * @param {string} filterId - Filter identifier
   * @param {number} strength - Filter strength 0-100
   * @returns {HTMLCanvasElement} New canvas with filtered result
   */
  applyFilter(sourceCanvas, filterId, strength) {
    const w = sourceCanvas.width;
    const h = sourceCanvas.height;
    const resultCanvas = document.createElement('canvas');
    resultCanvas.width = w;
    resultCanvas.height = h;
    const resultCtx = resultCanvas.getContext('2d');
    resultCtx.drawImage(sourceCanvas, 0, 0);

    if (filterId === 'original') return resultCanvas;

    const imageData = resultCtx.getImageData(0, 0, w, h);
    const data = imageData.data;
    const normalizedStrength = Math.max(0, Math.min(1, strength / 100));

    this.applyFilterById(data, filterId, normalizedStrength, w, h);

    resultCtx.putImageData(imageData, 0, 0);
    return resultCanvas;
  }

  /**
   * Dispatcher: apply filter by id to raw pixel data
   * @param {Uint8ClampedArray} data - Pixel data (RGBA)
   * @param {string} filterId - Filter identifier
   * @param {number} strength - 0-1 normalized strength
   * @param {number} width - Image width
   * @param {number} height - Image height
   */
  applyFilterById(data, filterId, strength, width, height) {
    switch (filterId) {
      case 'golden':
        this.applyGoldenFilter(data, strength, width, height);
        break;
      case 'pine':
        this.applyPineFilter(data, strength, width, height);
        break;
      case 'eternity':
        this.applyEternityFilter(data, strength, width, height);
        break;
      case 'coral':
        this.applyCoralFilter(data, strength, width, height);
        break;
      case 'moon':
        this.applyMoonFilter(data, strength, width, height);
        break;
      case 'forest':
        this.applyForestFilter(data, strength, width, height);
        break;
      case 'vintage':
        this.applyVintageFilter(data, strength, width, height);
        break;
      case 'blackwhite':
        this.applyBlackWhiteFilter(data, strength, width, height);
        break;
      case 'sepia':
        this.applySepiaFilter(data, strength, width, height);
        break;
      case 'warm':
        this.applyWarmFilter(data, strength, width, height);
        break;
      case 'cool':
        this.applyCoolFilter(data, strength, width, height);
        break;
      case 'popart':
        this.applyPopArtFilter(data, strength, width, height);
        break;
      case 'watercolor':
        this.applyWatercolorFilter(data, strength, width, height);
        break;
      case 'sketch':
        this.applySketchFilter(data, strength, width, height);
        break;
      case 'oilpainting':
        this.applyOilPaintingFilter(data, strength, width, height);
        break;
      case 'comic':
        this.applyComicFilter(data, strength, width, height);
        break;
      case 'impressionist':
        this.applyImpressionistFilter(data, strength, width, height);
        break;
      case 'vintageposter':
        this.applyVintagePosterFilter(data, strength, width, height);
        break;
      case 'stencil':
        this.applyStencilFilter(data, strength, width, height);
        break;
      default:
        // original or unknown - no-op
        break;
    }
  }

  // ============================================================
  // BASIC IMAGE PROCESSING HELPERS
  // ============================================================

  applyContrast(data, contrast) {
    const factor = (259 * (contrast + 255)) / (255 * (259 - contrast));
    for (let i = 0; i < data.length; i += 4) {
      data[i] = factor * (data[i] - 128) + 128;
      data[i + 1] = factor * (data[i + 1] - 128) + 128;
      data[i + 2] = factor * (data[i + 2] - 128) + 128;
    }
  }

  applyBrightness(data, brightness) {
    for (let i = 0; i < data.length; i += 4) {
      data[i] += brightness;
      data[i + 1] += brightness;
      data[i + 2] += brightness;
    }
  }

  applySaturation(data, saturation) {
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const gray = 0.299 * r + 0.587 * g + 0.114 * b;
      data[i] = gray + saturation * (r - gray);
      data[i + 1] = gray + saturation * (g - gray);
      data[i + 2] = gray + saturation * (b - gray);
    }
  }

  applyVignette(data, width, height, intensity) {
    const centerX = width / 2;
    const centerY = height / 2;
    const maxDist = Math.sqrt(centerX * centerX + centerY * centerY);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        const dx = x - centerX;
        const dy = y - centerY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const vignette = 1 - (dist / maxDist) * intensity;
        data[i] *= vignette;
        data[i + 1] *= vignette;
        data[i + 2] *= vignette;
      }
    }
  }

  // ============================================================
  // ADVANCED IMAGE PROCESSING HELPERS (Instagram-Grade Filters)
  // ============================================================

  buildCurveLUT(shadows, midtones, highlights) {
    shadows = shadows || 0;
    midtones = midtones || 0;
    highlights = highlights || 0;
    const lut = new Uint8Array(256);
    for (let i = 0; i < 256; i++) {
      const x = i / 255;
      let y = x;
      const shadowWeight = Math.pow(1 - x, 3);
      y += shadows * 0.3 * shadowWeight;
      const midWeight = Math.sin(x * Math.PI);
      y += midtones * 0.2 * midWeight;
      const highWeight = Math.pow(x, 3);
      y += highlights * 0.3 * highWeight;
      lut[i] = Math.max(0, Math.min(255, Math.round(y * 255)));
    }
    return lut;
  }

  applyToneCurve(data, lutR, lutG, lutB) {
    lutG = lutG || null;
    lutB = lutB || null;
    const gLut = lutG || lutR;
    const bLut = lutB || lutR;
    for (let i = 0; i < data.length; i += 4) {
      data[i] = lutR[data[i]];
      data[i + 1] = gLut[data[i + 1]];
      data[i + 2] = bLut[data[i + 2]];
    }
  }

  applySplitTones(data, shadowColor, highlightColor, balance, strength) {
    balance = balance || 0;
    strength = strength != null ? strength : 1;
    const midpoint = 0.5 + balance * 0.3;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      let shadowWeight, highlightWeight;
      if (lum < midpoint) {
        shadowWeight = 1 - (lum / midpoint);
        highlightWeight = 0;
      } else {
        shadowWeight = 0;
        highlightWeight = (lum - midpoint) / (1 - midpoint);
      }
      shadowWeight = Math.pow(shadowWeight, 1.5) * strength * 0.4;
      highlightWeight = Math.pow(highlightWeight, 1.5) * strength * 0.4;
      data[i] = r + (shadowColor.r - 128) * shadowWeight + (highlightColor.r - 128) * highlightWeight;
      data[i + 1] = g + (shadowColor.g - 128) * shadowWeight + (highlightColor.g - 128) * highlightWeight;
      data[i + 2] = b + (shadowColor.b - 128) * shadowWeight + (highlightColor.b - 128) * highlightWeight;
    }
  }

  applyLiftedBlacks(data, liftAmount, strength) {
    strength = strength != null ? strength : 1;
    const lift = liftAmount * strength;
    const scale = (255 - lift) / 255;
    for (let i = 0; i < data.length; i += 4) {
      data[i] = lift + data[i] * scale;
      data[i + 1] = lift + data[i + 1] * scale;
      data[i + 2] = lift + data[i + 2] * scale;
    }
  }

  applyHighlightRecovery(data, threshold, strength) {
    threshold = threshold != null ? threshold : 220;
    strength = strength != null ? strength : 1;
    const range = 255 - threshold;
    for (let i = 0; i < data.length; i += 4) {
      for (let c = 0; c < 3; c++) {
        const val = data[i + c];
        if (val > threshold) {
          const excess = val - threshold;
          const compressed = threshold + excess * (1 - strength * 0.7);
          data[i + c] = compressed;
        }
      }
    }
  }

  applyHSLGrading(data, adjustments, strength) {
    strength = strength != null ? strength : 1;
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const INV255 = 1 / 255;

    for (let i = 0; i < data.length; i += 4) {
      let r = data[i] * INV255;
      let g = data[i + 1] * INV255;
      let b = data[i + 2] * INV255;

      const max = r > g ? (r > b ? r : b) : (g > b ? g : b);
      const min = r < g ? (r < b ? r : b) : (g < b ? g : b);
      const d = max - min;
      const l = (max + min) * 0.5;
      let h = 0, s = 0;

      if (d > 0.004) {
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === r)      h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        else if (max === g) h = ((b - r) / d + 2) / 6;
        else                h = ((r - g) / d + 4) / 6;
      }

      let hNew = h, sNew = s, lNew = l;
      for (const adj of adjustments) {
        const hDiff = Math.abs(h - adj.hueCenter);
        const hDiffWrap = hDiff < 0.5 ? hDiff : 1 - hDiff;
        if (hDiffWrap <= adj.hueRange) {
          const weight = (1 - hDiffWrap / adj.hueRange) * strength;
          if (adj.hueShift) hNew += adj.hueShift * weight;
          if (adj.satMult) sNew *= 1 + (adj.satMult - 1) * weight;
          if (adj.lumMult) lNew *= 1 + (adj.lumMult - 1) * weight;
        }
      }

      hNew = ((hNew % 1) + 1) % 1;
      sNew = sNew < 0 ? 0 : (sNew > 1 ? 1 : sNew);
      lNew = lNew < 0 ? 0 : (lNew > 1 ? 1 : lNew);

      if (sNew < 0.001) {
        r = g = b = lNew;
      } else {
        const q = lNew < 0.5 ? lNew * (1 + sNew) : lNew + sNew - lNew * sNew;
        const p = 2 * lNew - q;
        r = hue2rgb(p, q, hNew + 1 / 3);
        g = hue2rgb(p, q, hNew);
        b = hue2rgb(p, q, hNew - 1 / 3);
      }

      data[i]     = r * 255;
      data[i + 1] = g * 255;
      data[i + 2] = b * 255;
    }
  }

  applyAdvancedVignette(data, width, height, options) {
    options = options || {};
    const intensity = options.intensity || 0.5;
    const roundness = options.roundness || 0.5;
    const feather = options.feather || 0.5;
    const color = options.color || { r: 0, g: 0, b: 0 };

    const centerX = width / 2;
    const centerY = height / 2;
    const aspect = width / height;

    const scaleX = roundness < 0.5 ? 1 : 1 / (1 + (roundness - 0.5) * (aspect - 1));
    const scaleY = roundness < 0.5 ? aspect : aspect / (1 + (roundness - 0.5) * (aspect - 1));

    const maxDist = Math.sqrt(centerX * centerX * scaleX * scaleX + centerY * centerY * scaleY * scaleY);
    const innerRadius = (1 - feather) * 0.7;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        const dx = (x - centerX) * scaleX;
        const dy = (y - centerY) * scaleY;
        const dist = Math.sqrt(dx * dx + dy * dy) / maxDist;

        let vignette = 1;
        if (dist > innerRadius) {
          const t = (dist - innerRadius) / (1 - innerRadius);
          const smoothT = t * t * (3 - 2 * t);
          vignette = 1 - smoothT * intensity;
        }

        data[i] = data[i] * vignette + color.r * (1 - vignette) * intensity;
        data[i + 1] = data[i + 1] * vignette + color.g * (1 - vignette) * intensity;
        data[i + 2] = data[i + 2] * vignette + color.b * (1 - vignette) * intensity;
      }
    }
  }

  applyClarity(data, width, height, amount, strength) {
    strength = strength != null ? strength : 1;
    if (amount === 0 || strength === 0) return;

    const effectiveAmount = amount * strength;

    const lum = new Float32Array(width * height);
    for (let i = 0; i < data.length; i += 4) {
      lum[i / 4] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }

    const radius = Math.max(2, Math.floor(Math.min(width, height) * 0.015));
    const blurred = new Float32Array(width * height);
    const temp = new Float32Array(width * height);

    // Horizontal pass
    for (let y = 0; y < height; y++) {
      let sum = 0;
      const rowStart = y * width;
      for (let x = 0; x <= radius; x++) {
        sum += lum[rowStart + x];
      }
      for (let x = 0; x < width; x++) {
        const left = x - radius - 1;
        const right = x + radius;
        if (right < width) sum += lum[rowStart + right];
        if (left >= 0) sum -= lum[rowStart + left];
        const count = Math.min(right, width - 1) - Math.max(left + 1, 0) + 1;
        temp[rowStart + x] = sum / count;
      }
    }

    // Vertical pass
    for (let x = 0; x < width; x++) {
      let sum = 0;
      for (let y = 0; y <= radius; y++) {
        sum += temp[y * width + x];
      }
      for (let y = 0; y < height; y++) {
        const top = y - radius - 1;
        const bottom = y + radius;
        if (bottom < height) sum += temp[bottom * width + x];
        if (top >= 0) sum -= temp[top * width + x];
        const count = Math.min(bottom, height - 1) - Math.max(top + 1, 0) + 1;
        blurred[y * width + x] = sum / count;
      }
    }

    for (let i = 0; i < data.length; i += 4) {
      const idx = i / 4;
      const diff = lum[idx] - blurred[idx];
      const boost = diff * effectiveAmount * 1.5;
      data[i] = Math.max(0, Math.min(255, data[i] + boost));
      data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + boost));
      data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + boost));
    }
  }

  applyFilmGrain(data, width, height, amount, size, strength) {
    size = size != null ? size : 1;
    strength = strength != null ? strength : 1;
    if (amount === 0 || strength === 0) return;

    const grainIntensity = amount * strength * 40;
    const step = Math.max(1, Math.round(size));

    let seed = 12345;
    const random = () => {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return (seed / 0x7fffffff) * 2 - 1;
    };

    const grainMap = new Float32Array(width * height);
    for (let y = 0; y < height; y += step) {
      for (let x = 0; x < width; x += step) {
        const grain = random() * grainIntensity;
        for (let dy = 0; dy < step && y + dy < height; dy++) {
          for (let dx = 0; dx < step && x + dx < width; dx++) {
            grainMap[(y + dy) * width + (x + dx)] = grain;
          }
        }
      }
    }

    for (let i = 0; i < data.length; i += 4) {
      const idx = i / 4;
      const lumVal = (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255;
      const lumWeight = Math.sin(lumVal * Math.PI) * 0.7 + 0.3;
      const grain = grainMap[idx] * lumWeight;
      data[i] = Math.max(0, Math.min(255, data[i] + grain));
      data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + grain));
      data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + grain));
    }
  }

  // ============================================================
  // ARTISTIC EFFECTS HELPER METHODS
  // ============================================================

  applyGaussianBlurCopy(data, width, height, sigma) {
    const output = new Float32Array(data.length);
    for (let i = 0; i < data.length; i++) output[i] = data[i];

    if (sigma < 0.5) return output;

    const radius = Math.ceil(sigma * 3);
    const kernel = [];
    let sum = 0;
    for (let i = -radius; i <= radius; i++) {
      const val = Math.exp(-(i * i) / (2 * sigma * sigma));
      kernel.push(val);
      sum += val;
    }
    for (let i = 0; i < kernel.length; i++) kernel[i] /= sum;

    const temp = new Float32Array(data.length);

    // Horizontal pass
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let r = 0, g = 0, b = 0;
        for (let k = -radius; k <= radius; k++) {
          const nx = Math.min(Math.max(x + k, 0), width - 1);
          const idx = (y * width + nx) * 4;
          const w = kernel[k + radius];
          r += data[idx] * w;
          g += data[idx + 1] * w;
          b += data[idx + 2] * w;
        }
        const idx = (y * width + x) * 4;
        temp[idx] = r;
        temp[idx + 1] = g;
        temp[idx + 2] = b;
        temp[idx + 3] = data[idx + 3];
      }
    }

    // Vertical pass
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let r = 0, g = 0, b = 0;
        for (let k = -radius; k <= radius; k++) {
          const ny = Math.min(Math.max(y + k, 0), height - 1);
          const idx = (ny * width + x) * 4;
          const w = kernel[k + radius];
          r += temp[idx] * w;
          g += temp[idx + 1] * w;
          b += temp[idx + 2] * w;
        }
        const idx = (y * width + x) * 4;
        output[idx] = r;
        output[idx + 1] = g;
        output[idx + 2] = b;
        output[idx + 3] = data[idx + 3];
      }
    }
    return output;
  }

  applyGaussianBlur(data, width, height, sigma) {
    const blurred = this.applyGaussianBlurCopy(data, width, height, sigma);
    for (let i = 0; i < data.length; i++) {
      data[i] = Math.round(blurred[i]);
    }
  }

  applyBilateralFilter(data, width, height, spatialSigma, rangeSigma) {
    const output = new Uint8ClampedArray(data.length);
    const radius = Math.ceil(spatialSigma * 2);
    const rangeSigma2 = 2 * rangeSigma * rangeSigma;
    const spatialSigma2 = 2 * spatialSigma * spatialSigma;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const centerIdx = (y * width + x) * 4;
        const centerR = data[centerIdx];
        const centerG = data[centerIdx + 1];
        const centerB = data[centerIdx + 2];

        let sumR = 0, sumG = 0, sumB = 0, sumWeight = 0;

        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const nx = Math.min(Math.max(x + dx, 0), width - 1);
            const ny = Math.min(Math.max(y + dy, 0), height - 1);
            const idx = (ny * width + nx) * 4;

            const r = data[idx];
            const g = data[idx + 1];
            const b = data[idx + 2];

            const spatialDist = dx * dx + dy * dy;
            const spatialWeight = Math.exp(-spatialDist / spatialSigma2);

            const colorDist = (r - centerR) ** 2 + (g - centerG) ** 2 + (b - centerB) ** 2;
            const rangeWeight = Math.exp(-colorDist / rangeSigma2);

            const weight = spatialWeight * rangeWeight;
            sumR += r * weight;
            sumG += g * weight;
            sumB += b * weight;
            sumWeight += weight;
          }
        }

        output[centerIdx] = Math.round(sumR / sumWeight);
        output[centerIdx + 1] = Math.round(sumG / sumWeight);
        output[centerIdx + 2] = Math.round(sumB / sumWeight);
        output[centerIdx + 3] = data[centerIdx + 3];
      }
    }

    for (let i = 0; i < data.length; i++) data[i] = output[i];
  }

  applyXDoG(data, width, height, sigma, k, p, epsilon, phi) {
    sigma = sigma || 1.0;
    k = k || 1.6;
    p = p || 19;
    epsilon = epsilon || 0.01;
    phi = phi || 10;

    const gray = new Float32Array(width * height);
    for (let i = 0; i < data.length; i += 4) {
      gray[i / 4] = (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255;
    }

    const grayData = new Float32Array(data.length);
    for (let i = 0; i < gray.length; i++) {
      const idx = i * 4;
      grayData[idx] = grayData[idx + 1] = grayData[idx + 2] = gray[i] * 255;
      grayData[idx + 3] = 255;
    }

    const gSmall = this.applyGaussianBlurCopy(grayData, width, height, sigma);
    const gLarge = this.applyGaussianBlurCopy(grayData, width, height, sigma * k);

    const dog = new Float32Array(width * height);
    for (let i = 0; i < gray.length; i++) {
      const idx = i * 4;
      const small = gSmall[idx] / 255;
      const large = gLarge[idx] / 255;
      dog[i] = (1 + p) * small - p * large;
    }

    for (let i = 0; i < dog.length; i++) {
      const d = dog[i];
      let val;
      if (d >= epsilon) {
        val = 1.0;
      } else {
        val = 1.0 + Math.tanh(phi * (d - epsilon));
      }
      const pixel = Math.max(0, Math.min(255, Math.round(val * 255)));
      const idx = i * 4;
      data[idx] = data[idx + 1] = data[idx + 2] = pixel;
    }
  }

  applyKuwaharaFilter(data, width, height, radius) {
    const output = new Uint8ClampedArray(data.length);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const quadrants = [
          { x1: -radius, x2: 0, y1: -radius, y2: 0 },
          { x1: 0, x2: radius, y1: -radius, y2: 0 },
          { x1: -radius, x2: 0, y1: 0, y2: radius },
          { x1: 0, x2: radius, y1: 0, y2: radius }
        ];

        let bestVariance = Infinity;
        let bestR = 0, bestG = 0, bestB = 0;

        for (const q of quadrants) {
          let sumR = 0, sumG = 0, sumB = 0;
          let sumR2 = 0, sumG2 = 0, sumB2 = 0;
          let count = 0;

          for (let dy = q.y1; dy <= q.y2; dy++) {
            for (let dx = q.x1; dx <= q.x2; dx++) {
              const nx = Math.min(Math.max(x + dx, 0), width - 1);
              const ny = Math.min(Math.max(y + dy, 0), height - 1);
              const idx = (ny * width + nx) * 4;
              const r = data[idx], g = data[idx + 1], b = data[idx + 2];
              sumR += r; sumG += g; sumB += b;
              sumR2 += r * r; sumG2 += g * g; sumB2 += b * b;
              count++;
            }
          }

          const avgR = sumR / count;
          const avgG = sumG / count;
          const avgB = sumB / count;
          const variance = (sumR2 + sumG2 + sumB2) / count -
            (avgR * avgR + avgG * avgG + avgB * avgB);

          if (variance < bestVariance) {
            bestVariance = variance;
            bestR = avgR; bestG = avgG; bestB = avgB;
          }
        }

        const idx = (y * width + x) * 4;
        output[idx] = Math.round(bestR);
        output[idx + 1] = Math.round(bestG);
        output[idx + 2] = Math.round(bestB);
        output[idx + 3] = data[idx + 3];
      }
    }
    for (let i = 0; i < data.length; i++) data[i] = output[i];
  }

  applyStrokeBasedRendering(data, width, height, brushRadius, maxStrokeLength, minStrokeLength) {
    const output = new Uint8ClampedArray(data.length);
    for (let i = 0; i < output.length; i += 4) {
      output[i] = output[i + 1] = output[i + 2] = 255;
      output[i + 3] = 255;
    }

    const gradX = new Float32Array(width * height);
    const gradY = new Float32Array(width * height);

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = y * width + x;
        const left = ((y * width + x - 1) * 4);
        const right = ((y * width + x + 1) * 4);
        const top = (((y - 1) * width + x) * 4);
        const bottom = (((y + 1) * width + x) * 4);

        const grayL = 0.299 * data[left] + 0.587 * data[left + 1] + 0.114 * data[left + 2];
        const grayR = 0.299 * data[right] + 0.587 * data[right + 1] + 0.114 * data[right + 2];
        const grayT = 0.299 * data[top] + 0.587 * data[top + 1] + 0.114 * data[top + 2];
        const grayB = 0.299 * data[bottom] + 0.587 * data[bottom + 1] + 0.114 * data[bottom + 2];

        gradX[idx] = (grayR - grayL) / 2;
        gradY[idx] = (grayB - grayT) / 2;
      }
    }

    const strokeSpacing = Math.max(1, Math.floor(brushRadius * 0.8));
    let seed = 42;
    const random = () => {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return seed / 0x7fffffff;
    };

    const strokes = [];
    for (let y = brushRadius; y < height - brushRadius; y += strokeSpacing) {
      for (let x = brushRadius; x < width - brushRadius; x += strokeSpacing) {
        const jx = x + (random() - 0.5) * strokeSpacing;
        const jy = y + (random() - 0.5) * strokeSpacing;
        if (jx >= brushRadius && jx < width - brushRadius &&
          jy >= brushRadius && jy < height - brushRadius) {
          strokes.push({ x: jx, y: jy, order: random() });
        }
      }
    }

    strokes.sort((a, b) => a.order - b.order);

    for (const stroke of strokes) {
      let cx = stroke.x;
      let cy = stroke.y;

      const srcIdx = (Math.floor(cy) * width + Math.floor(cx)) * 4;
      const strokeR = data[srcIdx];
      const strokeG = data[srcIdx + 1];
      const strokeB = data[srcIdx + 2];

      const points = [{ x: cx, y: cy }];

      for (let i = 0; i < maxStrokeLength; i++) {
        const gIdx = Math.floor(cy) * width + Math.floor(cx);
        const gx = gradX[gIdx] || 0;
        const gy = gradY[gIdx] || 0;
        const mag = Math.sqrt(gx * gx + gy * gy);

        if (mag < 0.01) break;

        const dx = -gy / mag;
        const dy = gx / mag;

        cx += dx * 1.5;
        cy += dy * 1.5;

        if (cx < brushRadius || cx >= width - brushRadius ||
          cy < brushRadius || cy >= height - brushRadius) break;

        points.push({ x: cx, y: cy });
      }

      if (points.length < minStrokeLength) continue;

      for (const pt of points) {
        for (let dy = -brushRadius; dy <= brushRadius; dy++) {
          for (let dx = -brushRadius; dx <= brushRadius; dx++) {
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist <= brushRadius) {
              const px = Math.floor(pt.x + dx);
              const py = Math.floor(pt.y + dy);
              if (px >= 0 && px < width && py >= 0 && py < height) {
                const idx = (py * width + px) * 4;
                const alpha = Math.max(0, 1 - dist / brushRadius);
                const blend = alpha * 0.7;
                output[idx] = Math.round(output[idx] * (1 - blend) + strokeR * blend);
                output[idx + 1] = Math.round(output[idx + 1] * (1 - blend) + strokeG * blend);
                output[idx + 2] = Math.round(output[idx + 2] * (1 - blend) + strokeB * blend);
              }
            }
          }
        }
      }
    }

    for (let i = 0; i < data.length; i++) data[i] = output[i];
  }

  applyColorQuantization(data, numColors) {
    const step = Math.floor(256 / Math.cbrt(numColors));
    for (let i = 0; i < data.length; i += 4) {
      data[i] = Math.round(Math.round(data[i] / step) * step);
      data[i + 1] = Math.round(Math.round(data[i + 1] / step) * step);
      data[i + 2] = Math.round(Math.round(data[i + 2] / step) * step);
    }
  }

  applyPosterize(data, levels) {
    const step = 255 / (levels - 1);
    for (let i = 0; i < data.length; i += 4) {
      data[i] = Math.round(Math.round(data[i] / step) * step);
      data[i + 1] = Math.round(Math.round(data[i + 1] / step) * step);
      data[i + 2] = Math.round(Math.round(data[i + 2] / step) * step);
    }
  }

  applySobelEdgeDetection(data, width, height) {
    const gray = new Float32Array(width * height);
    for (let i = 0; i < data.length; i += 4) {
      gray[i / 4] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }

    const sobelX = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
    const sobelY = [-1, -2, -1, 0, 0, 0, 1, 2, 1];
    const edges = new Float32Array(width * height);

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        let gx = 0, gy = 0;
        let ki = 0;
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const idx = (y + ky) * width + (x + kx);
            gx += gray[idx] * sobelX[ki];
            gy += gray[idx] * sobelY[ki];
            ki++;
          }
        }
        edges[y * width + x] = Math.sqrt(gx * gx + gy * gy);
      }
    }
    return edges;
  }

  applyCMYKHalftone(data, width, height, dotSize) {
    const output = new Uint8ClampedArray(data.length);

    for (let i = 0; i < output.length; i += 4) {
      output[i] = 255;
      output[i + 1] = 255;
      output[i + 2] = 255;
      output[i + 3] = 255;
    }

    const angles = {
      c: 15 * Math.PI / 180,
      m: 75 * Math.PI / 180,
      y: 0,
      k: 45 * Math.PI / 180
    };

    const channels = { c: [], m: [], y: [], k: [] };
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i] / 255;
      const g = data[i + 1] / 255;
      const b = data[i + 2] / 255;

      const kVal = 1 - Math.max(r, g, b);
      const c = kVal < 1 ? (1 - r - kVal) / (1 - kVal) : 0;
      const m = kVal < 1 ? (1 - g - kVal) / (1 - kVal) : 0;
      const yVal = kVal < 1 ? (1 - b - kVal) / (1 - kVal) : 0;

      channels.c.push(c);
      channels.m.push(m);
      channels.y.push(yVal);
      channels.k.push(kVal);
    }

    const spacing = dotSize;

    for (const [channel, angle] of Object.entries(angles)) {
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const channelData = channels[channel];

      for (let gy = -spacing; gy < height + spacing; gy += spacing) {
        for (let gx = -spacing; gx < width + spacing; gx += spacing) {
          const rx = Math.round(gx * cos - gy * sin);
          const ry = Math.round(gx * sin + gy * cos);

          if (rx < 0 || rx >= width || ry < 0 || ry >= height) continue;

          const sampleIdx = ry * width + rx;
          if (sampleIdx < 0 || sampleIdx >= channelData.length) continue;

          const val = channelData[sampleIdx];
          const radius = val * spacing * 0.45;

          for (let dy = -spacing / 2; dy <= spacing / 2; dy++) {
            for (let dx = -spacing / 2; dx <= spacing / 2; dx++) {
              const dist = Math.sqrt(dx * dx + dy * dy);
              if (dist <= radius) {
                const px = rx + dx;
                const py = ry + dy;
                if (px >= 0 && px < width && py >= 0 && py < height) {
                  const idx = (py * width + px) * 4;
                  if (channel === 'c') output[idx] = Math.max(0, output[idx] - 255);
                  if (channel === 'm') output[idx + 1] = Math.max(0, output[idx + 1] - 255);
                  if (channel === 'y') output[idx + 2] = Math.max(0, output[idx + 2] - 255);
                  if (channel === 'k') {
                    output[idx] = Math.max(0, output[idx] - 255);
                    output[idx + 1] = Math.max(0, output[idx + 1] - 255);
                    output[idx + 2] = Math.max(0, output[idx + 2] - 255);
                  }
                }
              }
            }
          }
        }
      }
    }

    for (let i = 0; i < data.length; i++) {
      data[i] = output[i];
    }
  }

  applyColorPalette(data, palette, dither) {
    dither = dither || false;
    const width = Math.sqrt(data.length / 4);

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      let minDist = Infinity;
      let closest = palette[0];
      for (const color of palette) {
        const dr = r - color.r;
        const dg = g - color.g;
        const db = b - color.b;
        const dist = dr * dr * 0.299 + dg * dg * 0.587 + db * db * 0.114;
        if (dist < minDist) {
          minDist = dist;
          closest = color;
        }
      }

      data[i] = closest.r;
      data[i + 1] = closest.g;
      data[i + 2] = closest.b;
    }
  }

  applyPaperTexture(data, width, height, strength) {
    let seed = 54321;
    const random = () => {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return seed / 0x7fffffff;
    };

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;
        const noise1 = (random() - 0.5) * 25 * strength;
        const noise2 = (random() - 0.5) * 12 * strength;
        const fiber = Math.sin(x * 0.5 + random() * 3) * 5 * strength;
        const texture = noise1 + noise2 + fiber;
        data[idx] = Math.max(0, Math.min(255, data[idx] + texture));
        data[idx + 1] = Math.max(0, Math.min(255, data[idx + 1] + texture));
        data[idx + 2] = Math.max(0, Math.min(255, data[idx + 2] + texture));
      }
    }
  }

  applyOilPaintEffect(data, width, height, radius, intensityLevels) {
    const output = new Uint8ClampedArray(data.length);
    const levels = intensityLevels || 20;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const intensityCount = new Array(levels).fill(0);
        const avgR = new Array(levels).fill(0);
        const avgG = new Array(levels).fill(0);
        const avgB = new Array(levels).fill(0);

        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const nx = Math.min(Math.max(x + dx, 0), width - 1);
            const ny = Math.min(Math.max(y + dy, 0), height - 1);
            const idx = (ny * width + nx) * 4;
            const r = data[idx], g = data[idx + 1], b = data[idx + 2];
            const intensity = Math.floor(((r + g + b) / 3) * levels / 256);
            const bin = Math.min(intensity, levels - 1);
            intensityCount[bin]++;
            avgR[bin] += r; avgG[bin] += g; avgB[bin] += b;
          }
        }

        let maxCount = 0, maxBin = 0;
        for (let i = 0; i < levels; i++) {
          if (intensityCount[i] > maxCount) { maxCount = intensityCount[i]; maxBin = i; }
        }

        const idx = (y * width + x) * 4;
        if (maxCount > 0) {
          output[idx] = Math.round(avgR[maxBin] / maxCount);
          output[idx + 1] = Math.round(avgG[maxBin] / maxCount);
          output[idx + 2] = Math.round(avgB[maxBin] / maxCount);
        } else {
          output[idx] = data[idx];
          output[idx + 1] = data[idx + 1];
          output[idx + 2] = data[idx + 2];
        }
        output[idx + 3] = data[idx + 3];
      }
    }

    for (let i = 0; i < data.length; i++) {
      data[i] = output[i];
    }
  }

  applyColorDodgeBlend(bottom, top, width, height) {
    for (let i = 0; i < bottom.length; i += 4) {
      for (let c = 0; c < 3; c++) {
        const b = bottom[i + c];
        const t = top[i + c];
        if (t === 255) {
          bottom[i + c] = 255;
        } else {
          bottom[i + c] = Math.min(255, Math.floor((b * 256) / (256 - t)));
        }
      }
    }
  }

  applyBrushStrokes(data, width, height, strokeLength, strokeWidth) {
    const output = new Uint8ClampedArray(data.length);

    for (let i = 0; i < data.length; i++) {
      output[i] = data[i];
    }

    const edges = this.applySobelEdgeDetection(data, width, height);

    const spacing = Math.max(2, strokeLength / 2);

    let seed = 12345;
    const random = () => {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return seed / 0x7fffffff;
    };

    for (let y = 0; y < height; y += spacing) {
      for (let x = 0; x < width; x += spacing) {
        const jx = x + (random() - 0.5) * spacing;
        const jy = y + (random() - 0.5) * spacing;

        if (jx < 0 || jx >= width || jy < 0 || jy >= height) continue;

        const srcIdx = (Math.floor(jy) * width + Math.floor(jx)) * 4;
        const r = data[srcIdx];
        const g = data[srcIdx + 1];
        const b = data[srcIdx + 2];

        const angle = random() * Math.PI;
        const len = strokeLength * (0.7 + random() * 0.6);

        for (let t = -len / 2; t < len / 2; t++) {
          const px = Math.round(jx + Math.cos(angle) * t);
          const py = Math.round(jy + Math.sin(angle) * t);

          for (let w = -strokeWidth / 2; w < strokeWidth / 2; w++) {
            const wx = Math.round(px + Math.cos(angle + Math.PI / 2) * w);
            const wy = Math.round(py + Math.sin(angle + Math.PI / 2) * w);

            if (wx >= 0 && wx < width && wy >= 0 && wy < height) {
              const idx = (wy * width + wx) * 4;
              const variation = (random() - 0.5) * 20;
              output[idx] = Math.max(0, Math.min(255, r + variation));
              output[idx + 1] = Math.max(0, Math.min(255, g + variation));
              output[idx + 2] = Math.max(0, Math.min(255, b + variation));
            }
          }
        }
      }
    }

    for (let i = 0; i < data.length; i++) {
      data[i] = output[i];
    }
  }

  // ============================================================
  // INSTAGRAM-GRADE FILTER IMPLEMENTATIONS
  // ============================================================

  applyGoldenFilter(data, strength, width, height) {
    const warmLut = this.buildCurveLUT(0.15, 0.1, -0.05);
    this.applyToneCurve(data, warmLut);

    this.applySplitTones(
      data,
      { r: 180, g: 140, b: 100 },
      { r: 255, g: 220, b: 150 },
      0.2,
      strength
    );

    this.applyHSLGrading(data, [
      { hueCenter: 0.08, hueRange: 0.1, satMult: 1.3, lumMult: 1.05 },
      { hueCenter: 0.15, hueRange: 0.08, satMult: 1.2, lumMult: 1.02 },
      { hueCenter: 0.55, hueRange: 0.1, satMult: 0.85 }
    ], strength);

    this.applyLiftedBlacks(data, 12, strength);

    this.applySaturation(data, 1 + (0.15 * strength));
    this.applyContrast(data, 10 * strength);

    this.applyAdvancedVignette(data, width, height, {
      intensity: 0.35 * strength,
      roundness: 0.6,
      feather: 0.6,
      color: { r: 40, g: 20, b: 0 }
    });

    this.applyFilmGrain(data, width, height, 0.15, 1, strength);
  }

  applyPineFilter(data, strength, width, height) {
    const coolLut = this.buildCurveLUT(0.1, 0.05, 0);
    this.applyToneCurve(data, coolLut);

    this.applySplitTones(
      data,
      { r: 100, g: 140, b: 140 },
      { r: 140, g: 150, b: 140 },
      -0.1,
      strength
    );

    this.applyHSLGrading(data, [
      { hueCenter: 0.33, hueRange: 0.15, satMult: 1.25, lumMult: 1.03, hueShift: -0.02 },
      { hueCenter: 0.45, hueRange: 0.1, satMult: 1.15 },
      { hueCenter: 0, hueRange: 0.08, satMult: 0.75 },
      { hueCenter: 0.08, hueRange: 0.06, satMult: 0.8 }
    ], strength);

    this.applyClarity(data, width, height, 0.25, strength);

    this.applyLiftedBlacks(data, 8, strength);

    this.applySaturation(data, 1 + (0.12 * strength));
    this.applyContrast(data, 8 * strength);

    this.applyAdvancedVignette(data, width, height, {
      intensity: 0.3 * strength,
      roundness: 0.5,
      feather: 0.55,
      color: { r: 10, g: 25, b: 20 }
    });
  }

  applyEternityFilter(data, strength, width, height) {
    const softLut = this.buildCurveLUT(0.25, -0.1, -0.15);
    this.applyToneCurve(data, softLut);

    this.applySplitTones(
      data,
      { r: 150, g: 130, b: 170 },
      { r: 255, g: 250, b: 240 },
      0,
      strength
    );

    this.applyHSLGrading(data, [
      { hueCenter: 0, hueRange: 0.5, satMult: 0.85 }
    ], strength);

    this.applyLiftedBlacks(data, 25, strength);

    this.applyClarity(data, width, height, -0.3, strength);

    this.applyContrast(data, -8 * strength);

    this.applyAdvancedVignette(data, width, height, {
      intensity: 0.2 * strength,
      roundness: 0.7,
      feather: 0.7,
      color: { r: 50, g: 40, b: 60 }
    });

    this.applyFilmGrain(data, width, height, 0.35, 1.2, strength);
  }

  applyCoralFilter(data, strength, width, height) {
    const warmLut = this.buildCurveLUT(0.05, 0.15, 0.05);
    this.applyToneCurve(data, warmLut);

    this.applySplitTones(
      data,
      { r: 190, g: 140, b: 130 },
      { r: 255, g: 230, b: 210 },
      0.15,
      strength
    );

    this.applyHSLGrading(data, [
      { hueCenter: 0.03, hueRange: 0.08, satMult: 1.35, lumMult: 1.02, hueShift: 0.01 },
      { hueCenter: 0.08, hueRange: 0.06, satMult: 1.3, lumMult: 1.03 },
      { hueCenter: 0.95, hueRange: 0.08, satMult: 1.25 },
      { hueCenter: 0.55, hueRange: 0.1, satMult: 0.9 }
    ], strength);

    this.applyLiftedBlacks(data, 8, strength);

    this.applySaturation(data, 1 + (0.22 * strength));
    this.applyContrast(data, 12 * strength);

    this.applyAdvancedVignette(data, width, height, {
      intensity: 0.25 * strength,
      roundness: 0.6,
      feather: 0.6,
      color: { r: 50, g: 25, b: 20 }
    });
  }

  applyMoonFilter(data, strength, width, height) {
    const coolLut = this.buildCurveLUT(-0.05, 0.15, 0.1);
    this.applyToneCurve(data, coolLut);

    this.applySplitTones(
      data,
      { r: 80, g: 100, b: 150 },
      { r: 200, g: 210, b: 230 },
      -0.2,
      strength
    );

    this.applyHSLGrading(data, [
      { hueCenter: 0.6, hueRange: 0.15, satMult: 1.15, lumMult: 0.95 },
      { hueCenter: 0.55, hueRange: 0.1, satMult: 1.1 },
      { hueCenter: 0.08, hueRange: 0.15, satMult: 0.6 },
      { hueCenter: 0, hueRange: 0.1, satMult: 0.65 }
    ], strength);

    this.applyClarity(data, width, height, 0.4, strength);

    this.applySaturation(data, 1 - (0.15 * strength));
    this.applyContrast(data, 15 * strength);

    this.applyAdvancedVignette(data, width, height, {
      intensity: 0.5 * strength,
      roundness: 0.5,
      feather: 0.45,
      color: { r: 10, g: 15, b: 35 }
    });
  }

  applyForestFilter(data, strength, width, height) {
    const richLut = this.buildCurveLUT(-0.05, 0.1, 0.05);
    this.applyToneCurve(data, richLut);

    this.applySplitTones(
      data,
      { r: 60, g: 90, b: 70 },
      { r: 170, g: 190, b: 140 },
      -0.15,
      strength
    );

    this.applyHSLGrading(data, [
      { hueCenter: 0.33, hueRange: 0.12, satMult: 1.35, lumMult: 0.95, hueShift: -0.03 },
      { hueCenter: 0.25, hueRange: 0.08, satMult: 1.2, lumMult: 0.98 },
      { hueCenter: 0.08, hueRange: 0.06, satMult: 1.1, hueShift: -0.01 },
      { hueCenter: 0.55, hueRange: 0.1, satMult: 0.8 }
    ], strength);

    this.applyClarity(data, width, height, 0.35, strength);

    this.applySaturation(data, 1 + (0.18 * strength));
    this.applyContrast(data, 12 * strength);

    this.applyAdvancedVignette(data, width, height, {
      intensity: 0.4 * strength,
      roundness: 0.45,
      feather: 0.5,
      color: { r: 15, g: 30, b: 15 }
    });
  }

  applyVintageFilter(data, strength, width, height) {
    const fadedLut = this.buildCurveLUT(0.2, -0.1, -0.1);
    this.applyToneCurve(data, fadedLut);

    this.applySplitTones(
      data,
      { r: 160, g: 130, b: 100 },
      { r: 250, g: 240, b: 220 },
      0.1,
      strength
    );

    this.applyHSLGrading(data, [
      { hueCenter: 0, hueRange: 0.5, satMult: 0.7 },
      { hueCenter: 0.08, hueRange: 0.1, hueShift: 0.02, satMult: 0.9 }
    ], strength);

    this.applyLiftedBlacks(data, 30, strength);

    this.applyContrast(data, -12 * strength);

    this.applyAdvancedVignette(data, width, height, {
      intensity: 0.55 * strength,
      roundness: 0.4,
      feather: 0.4,
      color: { r: 40, g: 25, b: 15 }
    });

    this.applyFilmGrain(data, width, height, 0.5, 1.3, strength);
  }

  applyBlackWhiteFilter(data, strength, width, height) {
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      const gray = 0.35 * r + 0.50 * g + 0.15 * b;

      const blended = r + (gray - r) * strength;
      const blendedG = g + (gray - g) * strength;
      const blendedB = b + (gray - b) * strength;

      data[i] = blended;
      data[i + 1] = blendedG;
      data[i + 2] = blendedB;
    }

    const sCurveLut = this.buildCurveLUT(-0.1, 0.25, 0.15);
    this.applyToneCurve(data, sCurveLut);

    this.applyLiftedBlacks(data, 15, strength);

    this.applyClarity(data, width, height, 0.5, strength);

    this.applyContrast(data, 18 * strength);

    this.applyAdvancedVignette(data, width, height, {
      intensity: 0.35 * strength,
      roundness: 0.5,
      feather: 0.5,
      color: { r: 0, g: 0, b: 0 }
    });

    this.applyFilmGrain(data, width, height, 0.3, 1, strength);
  }

  applySepiaFilter(data, strength, width, height) {
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      const tr = 0.40 * r + 0.77 * g + 0.19 * b;
      const tg = 0.35 * r + 0.69 * g + 0.17 * b;
      const tb = 0.27 * r + 0.53 * g + 0.13 * b;

      data[i] = r + (tr - r) * strength;
      data[i + 1] = g + (tg - g) * strength;
      data[i + 2] = b + (tb - b) * strength;
    }

    const sepiaLutR = this.buildCurveLUT(0.1, 0.1, 0);
    const sepiaLutG = this.buildCurveLUT(0.05, 0.05, -0.05);
    const sepiaLutB = this.buildCurveLUT(0, -0.05, -0.1);
    this.applyToneCurve(data, sepiaLutR, sepiaLutG, sepiaLutB);

    this.applyLiftedBlacks(data, 18, strength);

    this.applyContrast(data, 8 * strength);

    this.applyAdvancedVignette(data, width, height, {
      intensity: 0.5 * strength,
      roundness: 0.4,
      feather: 0.45,
      color: { r: 30, g: 18, b: 8 }
    });

    this.applyFilmGrain(data, width, height, 0.4, 1.2, strength);
  }

  applyWarmFilter(data, strength, width, height) {
    const warmLutR = this.buildCurveLUT(0.08, 0.15, 0.1);
    const warmLutG = this.buildCurveLUT(0.05, 0.1, 0.05);
    const warmLutB = this.buildCurveLUT(0, -0.1, -0.15);
    this.applyToneCurve(data, warmLutR, warmLutG, warmLutB);

    this.applySplitTones(
      data,
      { r: 170, g: 130, b: 100 },
      { r: 255, g: 240, b: 200 },
      0.1,
      strength
    );

    this.applyHSLGrading(data, [
      { hueCenter: 0.08, hueRange: 0.12, satMult: 1.25, lumMult: 1.02 },
      { hueCenter: 0.15, hueRange: 0.1, satMult: 1.2 },
      { hueCenter: 0, hueRange: 0.08, satMult: 1.15 },
      { hueCenter: 0.55, hueRange: 0.15, satMult: 0.8 }
    ], strength);

    this.applyHighlightRecovery(data, 230, strength * 0.5);

    this.applySaturation(data, 1 + (0.2 * strength));
    this.applyContrast(data, 10 * strength);

    this.applyAdvancedVignette(data, width, height, {
      intensity: 0.25 * strength,
      roundness: 0.6,
      feather: 0.6,
      color: { r: 50, g: 30, b: 10 }
    });
  }

  applyCoolFilter(data, strength, width, height) {
    const coolLutR = this.buildCurveLUT(0, -0.1, -0.1);
    const coolLutG = this.buildCurveLUT(0.02, 0.05, 0.05);
    const coolLutB = this.buildCurveLUT(0.05, 0.12, 0.15);
    this.applyToneCurve(data, coolLutR, coolLutG, coolLutB);

    this.applySplitTones(
      data,
      { r: 100, g: 120, b: 160 },
      { r: 200, g: 230, b: 250 },
      0.05,
      strength
    );

    this.applyHSLGrading(data, [
      { hueCenter: 0.55, hueRange: 0.12, satMult: 1.2, lumMult: 1.02 },
      { hueCenter: 0.65, hueRange: 0.15, satMult: 1.15 },
      { hueCenter: 0.75, hueRange: 0.1, satMult: 1.1 },
      { hueCenter: 0.08, hueRange: 0.12, satMult: 0.8 }
    ], strength);

    this.applyClarity(data, width, height, 0.2, strength);

    this.applySaturation(data, 1 + (0.15 * strength));
    this.applyContrast(data, 12 * strength);

    this.applyAdvancedVignette(data, width, height, {
      intensity: 0.3 * strength,
      roundness: 0.55,
      feather: 0.55,
      color: { r: 15, g: 25, b: 45 }
    });
  }

  // ============================================================
  // ARTISTIC EFFECTS FILTER IMPLEMENTATIONS
  // ============================================================

  applyPopArtFilter(data, strength, width, height) {
    const original = new Uint8ClampedArray(data);

    this.applyContrast(data, 50);

    this.applySaturation(data, 2.2);

    const popPalette = [
      { r: 255, g: 20, b: 147 },
      { r: 255, g: 255, b: 0 },
      { r: 0, g: 255, b: 255 },
      { r: 255, g: 165, b: 0 },
      { r: 138, g: 43, b: 226 },
      { r: 50, g: 205, b: 50 },
      { r: 255, g: 0, b: 0 },
      { r: 0, g: 0, b: 0 },
      { r: 255, g: 255, b: 255 }
    ];

    this.applyPosterize(data, 6);

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      let minDist = Infinity;
      let closest = popPalette[0];
      for (const color of popPalette) {
        const dr = (r - color.r) * 0.299;
        const dg = (g - color.g) * 0.587;
        const db = (b - color.b) * 0.114;
        const dist = dr * dr + dg * dg + db * db;
        if (dist < minDist) {
          minDist = dist;
          closest = color;
        }
      }
      data[i] = closest.r;
      data[i + 1] = closest.g;
      data[i + 2] = closest.b;
    }

    for (let i = 0; i < data.length; i += 4) {
      data[i] = original[i] + (data[i] - original[i]) * strength;
      data[i + 1] = original[i + 1] + (data[i + 1] - original[i + 1]) * strength;
      data[i + 2] = original[i + 2] + (data[i + 2] - original[i + 2]) * strength;
    }
  }

  applyWatercolorFilter(data, strength, width, height) {
    const original = new Uint8ClampedArray(data);

    this.applyGaussianBlur(data, width, height, 2);

    this.applyPosterize(data, 12);

    this.applySaturation(data, 0.75);

    this.applyLiftedBlacks(data, 35, 1.0);

    this.applyGaussianBlur(data, width, height, 1.5);

    this.applyPaperTexture(data, width, height, 0.5);

    for (let i = 0; i < data.length; i += 4) {
      data[i] = original[i] + (data[i] - original[i]) * strength;
      data[i + 1] = original[i + 1] + (data[i + 1] - original[i + 1]) * strength;
      data[i + 2] = original[i + 2] + (data[i + 2] - original[i + 2]) * strength;
    }
  }

  applySketchFilter(data, strength, width, height) {
    const original = new Uint8ClampedArray(data);

    for (let i = 0; i < data.length; i += 4) {
      const gray = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
      data[i] = gray;
      data[i + 1] = gray;
      data[i + 2] = gray;
    }

    const inverted = new Uint8ClampedArray(data.length);
    for (let i = 0; i < data.length; i += 4) {
      inverted[i] = 255 - data[i];
      inverted[i + 1] = 255 - data[i + 1];
      inverted[i + 2] = 255 - data[i + 2];
      inverted[i + 3] = data[i + 3];
    }

    this.applyGaussianBlur(inverted, width, height, 3);

    for (let i = 0; i < data.length; i += 4) {
      for (let c = 0; c < 3; c++) {
        const b = data[i + c];
        const t = inverted[i + c];
        data[i + c] = t === 255 ? 255 : Math.min(255, Math.floor((b * 256) / (256 - t)));
      }
    }

    this.applyContrast(data, 30);

    for (let i = 0; i < data.length; i += 4) {
      data[i] = Math.min(255, data[i] + 12);
      data[i + 1] = Math.min(255, data[i + 1] + 8);
      data[i + 2] = Math.max(0, data[i + 2] - 5);
    }

    this.applyPaperTexture(data, width, height, 0.2);

    for (let i = 0; i < data.length; i += 4) {
      data[i] = original[i] + (data[i] - original[i]) * strength;
      data[i + 1] = original[i + 1] + (data[i + 1] - original[i + 1]) * strength;
      data[i + 2] = original[i + 2] + (data[i + 2] - original[i + 2]) * strength;
    }
  }

  applyOilPaintingFilter(data, strength, width, height) {
    const original = new Uint8ClampedArray(data);

    const radius = 3;
    this.applyOilPaintEffect(data, width, height, radius, 20);

    this.applySaturation(data, 1.3);

    this.applyContrast(data, 15);

    this.applyClarity(data, width, height, 0.3, 1.0);

    this.applyPaperTexture(data, width, height, 0.35);

    this.applyAdvancedVignette(data, width, height, {
      intensity: 0.2,
      roundness: 0.5,
      feather: 0.6,
      color: { r: 25, g: 15, b: 5 }
    });

    for (let i = 0; i < data.length; i += 4) {
      data[i] = original[i] + (data[i] - original[i]) * strength;
      data[i + 1] = original[i + 1] + (data[i + 1] - original[i + 1]) * strength;
      data[i + 2] = original[i + 2] + (data[i + 2] - original[i + 2]) * strength;
    }
  }

  applyComicFilter(data, strength, width, height) {
    const original = new Uint8ClampedArray(data);

    const edges = this.applySobelEdgeDetection(data, width, height);

    this.applyContrast(data, 40);

    this.applySaturation(data, 1.5);

    this.applyPosterize(data, 5);

    for (let i = 0; i < data.length; i += 4) {
      const edge = edges[i / 4];
      if (edge > 50) {
        const outline = Math.min(1, (edge - 50) / 100);
        data[i] = Math.round(data[i] * (1 - outline));
        data[i + 1] = Math.round(data[i + 1] * (1 - outline));
        data[i + 2] = Math.round(data[i + 2] * (1 - outline));
      }
    }

    for (let i = 0; i < data.length; i += 4) {
      data[i] = original[i] + (data[i] - original[i]) * strength;
      data[i + 1] = original[i + 1] + (data[i + 1] - original[i + 1]) * strength;
      data[i + 2] = original[i + 2] + (data[i + 2] - original[i + 2]) * strength;
    }
  }

  applyImpressionistFilter(data, strength, width, height) {
    const original = new Uint8ClampedArray(data);

    this.applyGaussianBlur(data, width, height, 1.5);

    this.applySaturation(data, 1.4);

    for (let i = 0; i < data.length; i += 4) {
      data[i] = Math.min(255, data[i] + 12);
      data[i + 1] = Math.min(255, data[i + 1] + 6);
      data[i + 2] = Math.max(0, data[i + 2] - 8);
    }

    let seed = 33333;
    const random = () => {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return seed / 0x7fffffff;
    };

    for (let i = 0; i < data.length; i += 4) {
      const variation = (random() - 0.5) * 20;
      data[i] = Math.max(0, Math.min(255, data[i] + variation));
      data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + variation * 0.5));
      data[i + 2] = Math.max(0, Math.min(255, data[i + 2] - variation * 0.5));
    }

    this.applyContrast(data, 15);

    this.applyPaperTexture(data, width, height, 0.3);

    for (let i = 0; i < data.length; i += 4) {
      data[i] = original[i] + (data[i] - original[i]) * strength;
      data[i + 1] = original[i + 1] + (data[i + 1] - original[i + 1]) * strength;
      data[i + 2] = original[i + 2] + (data[i + 2] - original[i + 2]) * strength;
    }
  }

  applyVintagePosterFilter(data, strength, width, height) {
    const original = new Uint8ClampedArray(data);

    this.applyContrast(data, 35);

    this.applySplitTones(
      data,
      { r: 140, g: 100, b: 70 },
      { r: 240, g: 220, b: 180 },
      0.1,
      1.0
    );

    this.applyPosterize(data, 5);

    const vintagePalette = [
      { r: 35, g: 25, b: 15 },
      { r: 120, g: 80, b: 50 },
      { r: 180, g: 140, b: 90 },
      { r: 230, g: 200, b: 150 },
      { r: 245, g: 240, b: 220 },
      { r: 170, g: 60, b: 40 },
      { r: 80, g: 100, b: 80 },
      { r: 50, g: 70, b: 90 },
      { r: 200, g: 100, b: 50 }
    ];
    this.applyColorPalette(data, vintagePalette);

    this.applyPaperTexture(data, width, height, 0.45);

    this.applyAdvancedVignette(data, width, height, {
      intensity: 0.5,
      roundness: 0.4,
      feather: 0.4,
      color: { r: 30, g: 20, b: 10 }
    });

    for (let i = 0; i < data.length; i += 4) {
      data[i] = original[i] + (data[i] - original[i]) * strength;
      data[i + 1] = original[i + 1] + (data[i + 1] - original[i + 1]) * strength;
      data[i + 2] = original[i + 2] + (data[i + 2] - original[i + 2]) * strength;
    }
  }

  applyStencilFilter(data, strength, width, height) {
    const original = new Uint8ClampedArray(data);

    for (let i = 0; i < data.length; i += 4) {
      const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      data[i] = gray;
      data[i + 1] = gray;
      data[i + 2] = gray;
    }

    this.applyContrast(data, 60);

    const levels = [60, 120, 180];
    const colors = [
      { r: 0, g: 0, b: 0 },
      { r: 80, g: 80, b: 80 },
      { r: 180, g: 180, b: 180 },
      { r: 255, g: 255, b: 255 }
    ];

    for (let i = 0; i < data.length; i += 4) {
      const val = data[i];
      let color;
      if (val < levels[0]) {
        color = colors[0];
      } else if (val < levels[1]) {
        color = colors[1];
      } else if (val < levels[2]) {
        color = colors[2];
      } else {
        color = colors[3];
      }
      data[i] = color.r;
      data[i + 1] = color.g;
      data[i + 2] = color.b;
    }

    let seed = 99999;
    const random = () => {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return seed / 0x7fffffff;
    };

    for (let i = 0; i < data.length; i += 4) {
      const grayLevel = data[i];
      if (grayLevel < 200 && grayLevel > 20) {
        if (random() < 0.02) {
          const adjustment = (random() - 0.5) * 60;
          data[i] = Math.max(0, Math.min(255, data[i] + adjustment));
          data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + adjustment));
          data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + adjustment));
        }
      }
    }

    for (let i = 0; i < data.length; i += 4) {
      data[i] = original[i] + (data[i] - original[i]) * strength;
      data[i + 1] = original[i + 1] + (data[i + 1] - original[i + 1]) * strength;
      data[i + 2] = original[i + 2] + (data[i + 2] - original[i + 2]) * strength;
    }
  }
}

// Instantiate as global
window.ptpFilterEngine = new PTPFilterEngine();
